.hl {
  width: 100%;
  height: 1px;
  border-top: 1px solid var(--x-color-border);
}

.vl {
  width: 1px;
  height: auto;
  border-left: 1px solid var(--x-color-border);
}

.commaSeparated {
  display: content;

  a:not(:last-child):after {
    content: ", ";
  }
}

.attention {
  padding: 10px;
  background-color: var(--x-color-background-red);

  :global(.colored) {
    color: var(--x-color-text-red-active);
    font-weight: 600;
  }
}

.colFullPage {
  min-height: calc(100vh - var(--header-height));
}

.container {
  padding-top: var(--header-height);
}

.mainContentColumn {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
}

/* stylelint-disable selector-class-pattern -- erp*ShellMarker are runtime DOM markers set elsewhere; they MUST stay :global so the hashed CSS-Module scope never breaks the :has() cascade match. Migrated verbatim from emotion shared/styled.ts (Plan 03-05). Constants inlined: sizes.headerHeight=50px, size.laptop=1024px. */

.mainWithCompactMessengerRow {
  display: flex;
  width: 100%;
  min-width: 0;
  min-height: calc(100vh - 50px);
  flex: 1;
  flex-direction: row;

  &:has(:global(.erpCompactMessengerShellMarker)) {
    max-height: calc(100vh - 50px);
    /* Default for non-deal pages (workspaces, billing, etc.): allow row-level scroll so tall
     * content stays accessible. The overflow-y:auto + max-height pair also resolves the EUO-220
     * touchpad freeze — overflow-x:auto implicitly computes overflow-y:auto, and without a height
     * ceiling the container has no vertical overflow, so touchpad deltaX events get absorbed. */
    overflow-x: auto;
    overflow-y: auto;
  }

  /* MainContentColumn is the first child in BaseController */

  &:has(:global(.erpCompactMessengerShellMarker)) > *:first-child {
    min-width: 1024px;
  }

  /* Deal-page-specific layout (EUO-231 Bug 7). When the deal page is mounted and compact messenger
   * is open, we cannot scroll the whole row vertically — the deal left sidebar needs its own scroll
   * so the user can reach all objects without dragging the deal content too. Lock the row to viewport
   * height, switch row overflow-y to hidden, and cascade the height constraint down through the
   * MainContentColumn → dealBody → page chain so DealLeftSidebarLayout floatingScroll (flex:1 +
   * overflow-y:auto) finally has a definite parent height to scroll within. */

  &:has(:global(.erpCompactMessengerShellMarker)):has(:global(.erpDealBodyShellMarker)) {
    height: calc(100vh - 50px);
    overflow-y: hidden;
  }

  &:has(:global(.erpCompactMessengerShellMarker)):has(:global(.erpDealBodyShellMarker)) > *:first-child {
    overflow: hidden;
    height: 100%;
    min-height: 0;
  }

  &:has(:global(.erpCompactMessengerShellMarker)) :global(.erpDealBodyShellMarker) {
    height: 100%;
    min-height: 0;
    max-height: 100%;
  }

  &:has(:global(.erpCompactMessengerShellMarker)) :global(.erpDealPageShellMarker) {
    min-height: 0;
    max-height: 100%;
    flex: 1 1 0;
  }

  /* The deal left sidebar (DealLeftSidebarLayout root) is the first child of the deal page. It owns its
   * own scroll via an inner flex container (.columnBody in fixed mode, .floatingScroll in compact-rail
   * mode), both flex: 1 + overflow-y: auto. Those only scroll when their parent has a definite height.
   * Relying on align-self: stretch alone proved unreliable — the object list could not be scrolled to
   * the bottom when the compact messenger was open (EUO-231 Bug 7, 28.05 retest: Sidebar Mode sidebar
   * still not scrollable). Pin the sidebar root to the row height so the inner scroller is bounded. */

  &:has(:global(.erpCompactMessengerShellMarker)) :global(.erpDealPageShellMarker) > *:first-child {
    height: 100%;
    min-height: 0;
  }

  /* Give the deal main content column its own vertical scroll so sticky inner headers (object
   * page headers, deal sub-tabs, etc.) anchor to the column instead of disappearing with the page
   * scroll. Without this, the constrained dealBody chain has no scroll ancestor — sticky elements
   * inside the content column behave like static and ride away with the content (EUO-231 Bug 3). */

  &:has(:global(.erpCompactMessengerShellMarker)) :global(.erpDealContentShellMarker) {
    min-height: 0;
    max-height: 100%;
    overflow-y: auto;
  }

  /* PrimaryHeader (object page header — Quote, OC, PO, etc.) is sticky with top: headerHeight so it sits
   * below the OMS top bar during document-body scrolling. Inside the compact layout the deal content
   * column is the scroll ancestor; that legacy offset then leaves a visible gap above the header and
   * the user sees the header anchored a row or two below the column edge. Reset top so the header sits
   * flush against the column when scrolling within compact (EUO-231 Bug 3 follow-up). The pinned and
   * deal-banner variants ship their own calc() top values (same specificity as the base rule), so use
   * !important here to win the cascade regardless of which variant the page currently renders. */

  &:has(:global(.erpCompactMessengerShellMarker)) :global(.erpDealPrimaryHeaderShellMarker) {
    /* stylelint-disable-next-line declaration-no-important -- landmine preserved verbatim from emotion source: !important wins the variant cascade (pinned / deal-banner top calc() variants) */
    top: 0 !important;
  }

  /*
   * At viewports narrower than (size.laptop + compact-messenger-max = 1024 + 554 = 1578px) the
   * deal right sidebar is pushed behind the fixed compact messenger panel, hiding its content and
   * squeezing the Positions section below a usable width. Hide it so the content column can fill
   * the available space. The sidebar is still accessible when the messenger is closed or on wider
   * screens where both fit without overlap.
   */

  @media screen and (max-width: 1577px) {
    &:has(:global(.erpCompactMessengerShellMarker)) :global(.erpDealRightSidebarShellMarker) {
      display: none;
    }
  }
}
/* stylelint-enable selector-class-pattern */
